import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Plus, Power, Copy, CheckCircle2, Loader2, Search, Gift } from "lucide-react";
import { toast } from "sonner";

export default function AdminCodesPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newType, setNewType] = useState("trial");
  const [newDays, setNewDays] = useState(90);
  const [newMaxUses, setNewMaxUses] = useState(1);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);

  const fetchCodes = async () => {
    try {
      const r = await axios.get(`${API}/admin/redeem-codes`);
      setCodes(r.data.codes || []);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
      else toast.error("Failed to load codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/start");
      return;
    }
    fetchCodes();
    // eslint-disable-next-line
  }, [authLoading, user]);

  const create = async (e) => {
    e.preventDefault();
    if (!newCode.trim()) return toast.error("Code is required");
    setCreating(true);
    try {
      await axios.post(`${API}/admin/redeem-codes/create`, {
        code: newCode.trim(),
        type: newType,
        days: Number(newDays) || 90,
        max_uses: Number(newMaxUses) || 1,
      });
      toast.success(`Created ${newCode.toUpperCase()}`);
      setNewCode("");
      setNewType("trial");
      setNewDays(90);
      setNewMaxUses(1);
      setShowCreate(false);
      fetchCodes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (code, nextActive) => {
    try {
      await axios.patch(`${API}/admin/redeem-codes/${code}`, { active: nextActive });
      toast.success(`${code} ${nextActive ? "activated" : "deactivated"}`);
      fetchCodes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Toggle failed");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 1500);
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return codes;
    const q = query.toLowerCase();
    return codes.filter((c) => (c.code || "").toLowerCase().includes(q));
  }, [codes, query]);

  const stats = useMemo(() => {
    const total = codes.length;
    const used = codes.reduce((s, c) => s + (c.uses || 0), 0);
    const capacity = codes.reduce((s, c) => s + (c.max_uses || 1), 0);
    const lifetime = codes.filter((c) => c.type === "lifetime").length;
    return { total, used, capacity, lifetime };
  }, [codes]);

  if (forbidden) {
    return (
      <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="admin-forbidden">
          <p className="font-mono-eyebrow text-[10px] uppercase tracking-wider text-[#b94e4e] mb-3">Access denied</p>
          <h1 className="font-display text-[26px] text-[#0f1724] font-semibold tracking-tight leading-tight mb-3">
            Admins only
          </h1>
          <p className="font-body text-[14px] text-[#4b5563] mb-6">
            This dashboard is restricted to admin accounts.
          </p>
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#486a8f] text-[13px] hover:underline">
            <ArrowLeft size={14} /> Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center">
        <Loader2 size={28} className="text-[#486a8f] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f3ea] px-6 py-10" data-testid="admin-codes-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#486a8f] text-[13px] mb-3 hover:underline" data-testid="admin-back-link">
              <ArrowLeft size={14} /> Dashboard
            </Link>
            <p className="font-mono-eyebrow text-[10px] uppercase tracking-wider text-[#486a8f] mb-2">Admin</p>
            <h1 className="font-display text-[34px] text-[#0f1724] font-semibold tracking-tight leading-tight">
              Redeem codes
            </h1>
          </div>
          <Button
            data-testid="admin-create-code-btn"
            onClick={() => setShowCreate((v) => !v)}
            className="h-11 px-5 rounded-full bg-[#3e6b52] hover:bg-[#2f5440] text-white font-medium text-[13px]"
          >
            <Plus size={14} className="mr-1.5" /> {showCreate ? "Cancel" : "New code"}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white border border-[#eadfc9] rounded-xl p-4" data-testid="admin-stat-total">
            <p className="font-mono-eyebrow text-[9px] uppercase text-[#486a8f] mb-1">Total codes</p>
            <p className="font-display text-[26px] text-[#0f1724] font-semibold">{stats.total}</p>
          </div>
          <div className="bg-white border border-[#eadfc9] rounded-xl p-4" data-testid="admin-stat-used">
            <p className="font-mono-eyebrow text-[9px] uppercase text-[#486a8f] mb-1">Redemptions</p>
            <p className="font-display text-[26px] text-[#0f1724] font-semibold">{stats.used}</p>
            <p className="font-body text-[11px] text-[#6b7280]">of {stats.capacity} capacity</p>
          </div>
          <div className="bg-white border border-[#eadfc9] rounded-xl p-4" data-testid="admin-stat-lifetime">
            <p className="font-mono-eyebrow text-[9px] uppercase text-[#486a8f] mb-1">Lifetime codes</p>
            <p className="font-display text-[26px] text-[#0f1724] font-semibold">{stats.lifetime}</p>
          </div>
          <div className="bg-white border border-[#eadfc9] rounded-xl p-4" data-testid="admin-stat-trial">
            <p className="font-mono-eyebrow text-[9px] uppercase text-[#486a8f] mb-1">Trial codes</p>
            <p className="font-display text-[26px] text-[#0f1724] font-semibold">{stats.total - stats.lifetime}</p>
          </div>
        </div>

        {/* Create form */}
        {showCreate && (
          <form onSubmit={create} className="bg-white border border-[#eadfc9] rounded-xl p-5 mb-6 space-y-3" data-testid="admin-create-form">
            <h3 className="font-display text-[18px] text-[#0f1724] font-semibold">Create a new code</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2">
                <label className="font-mono-eyebrow text-[10px] text-[#486a8f] block mb-1.5">CODE</label>
                <Input
                  data-testid="admin-create-code-input"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  placeholder="e.g. VIP-LAUNCH"
                  className="h-10 uppercase"
                  required
                />
              </div>
              <div>
                <label className="font-mono-eyebrow text-[10px] text-[#486a8f] block mb-1.5">TYPE</label>
                <select
                  data-testid="admin-create-type-select"
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm"
                >
                  <option value="trial">Trial (N days)</option>
                  <option value="lifetime">Lifetime</option>
                </select>
              </div>
              <div>
                <label className="font-mono-eyebrow text-[10px] text-[#486a8f] block mb-1.5">
                  {newType === "lifetime" ? "DAYS (auto)" : "DAYS"}
                </label>
                <Input
                  data-testid="admin-create-days-input"
                  type="number"
                  value={newType === "lifetime" ? 36500 : newDays}
                  onChange={(e) => setNewDays(e.target.value)}
                  disabled={newType === "lifetime"}
                  className="h-10"
                  min={1}
                />
              </div>
              <div>
                <label className="font-mono-eyebrow text-[10px] text-[#486a8f] block mb-1.5">MAX USES</label>
                <Input
                  data-testid="admin-create-max-uses-input"
                  type="number"
                  value={newMaxUses}
                  onChange={(e) => setNewMaxUses(e.target.value)}
                  className="h-10"
                  min={1}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                data-testid="admin-create-submit-btn"
                type="submit"
                disabled={creating || !newCode.trim()}
                className="h-10 px-5 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium text-[13px]"
              >
                {creating ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Creating…</> : "Create code"}
              </Button>
            </div>
          </form>
        )}

        {/* Search */}
        <div className="relative mb-4">
          <Input
            data-testid="admin-codes-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search codes…"
            className="h-11 pl-10"
          />
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#486a8f]" />
        </div>

        {/* Table */}
        <div className="bg-white border border-[#eadfc9] rounded-xl overflow-hidden" data-testid="admin-codes-table">
          <table className="w-full">
            <thead>
              <tr className="bg-[#faf6ec] text-left text-[11px] uppercase tracking-wider text-[#486a8f] font-mono-eyebrow">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Days</th>
                <th className="px-4 py-3">Uses</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-[13px] text-[#6b7280]">
                    No codes match that search.
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                  <tr key={c.code} className="border-t border-[#f3ecd9]" data-testid={`admin-code-row-${c.code}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Gift size={13} className="text-[#486a8f] shrink-0" />
                        <span className="font-mono text-[13px] text-[#0f1724] font-medium">{c.code}</span>
                        <button
                          data-testid={`admin-code-copy-${c.code}`}
                          onClick={() => copyCode(c.code)}
                          className="text-[#486a8f] hover:text-[#3a5a7c]"
                          title="Copy"
                        >
                          {copied === c.code ? <CheckCircle2 size={13} /> : <Copy size={13} />}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-[#2b3442]">{c.type}</td>
                    <td className="px-4 py-3 text-[13px] text-[#2b3442]">
                      {c.type === "lifetime" ? "∞" : c.days}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-[#2b3442]">
                      <span className="font-medium">{c.uses}</span>
                      <span className="text-[#9ca3af]"> / {c.max_uses}</span>
                    </td>
                    <td className="px-4 py-3">
                      {c.active !== false ? (
                        <span className="inline-flex items-center gap-1 bg-[#e5f0e7] text-[#2f5440] px-2 py-0.5 rounded-full text-[11px] font-medium">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-[#f5e9e9] text-[#b94e4e] px-2 py-0.5 rounded-full text-[11px] font-medium">
                          Disabled
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`admin-code-toggle-${c.code}`}
                        onClick={() => toggle(c.code, c.active === false)}
                        className="inline-flex items-center gap-1.5 text-[12px] text-[#486a8f] hover:text-[#3a5a7c]"
                      >
                        <Power size={12} />
                        {c.active !== false ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
